import { formatCivilDate } from '@hue/design-system';
import { useQuery } from '@tanstack/react-query';
import {
  onlinePaymentsApi,
  type OnlineCharge,
  type Sale,
  type SalePayment,
} from '../../lib/api.js';

export const PAYMENT_REFRESH_MS = 5_000;

/// A tela acompanha somente pagamentos online que ainda podem receber confirmação ou estorno.
export function tracksOnlinePayments(sale: Sale): boolean {
  return sale.payments.some(
    (payment) =>
      payment.method === 'ONLINE' &&
      ['PENDING', 'AUTHORIZED', 'CONFIRMED'].includes(payment.status),
  );
}

/// Cobrança online (ADR 0060): o estado da conta da unidade e as cobranças desta venda.
export function useOnlineCharges(sale: Sale, enabled: boolean) {
  const status = useQuery({
    queryKey: ['company', 'online-payments', 'status'],
    queryFn: () => onlinePaymentsApi.status(),
    enabled,
    retry: false,
  });
  const charges = useQuery({
    queryKey: ['company', 'sales', sale.id, 'online-charges'],
    queryFn: () => onlinePaymentsApi.charges(sale.id),
    enabled: sale.payments.some((payment) => payment.method === 'ONLINE'),
    refetchInterval: tracksOnlinePayments(sale) ? PAYMENT_REFRESH_MS : false,
    retry: false,
  });
  const byPayment = new Map(
    (charges.data ?? []).map((charge) => [charge.salePaymentId, charge] as const),
  );
  return { status: status.data, byPayment, refetch: () => charges.refetch() };
}

const PAID_WITH: Record<NonNullable<OnlineCharge['paidWith']>, string> = {
  PIX: 'Pix',
  BOLETO: 'boleto',
  CARD: 'cartão',
  OTHER: 'outro meio',
};

/// O título da linha do pagamento online: como a cliente pagou, quando já se sabe.
export function onlinePaymentTitle(charge: OnlineCharge | undefined): string {
  return charge?.paidWith
    ? `Cobrança online · paga por ${PAID_WITH[charge.paidWith]}`
    : 'Cobrança online · Pix ou link';
}

/// O que a linha diz e como o selo aparece, conforme a cobrança e o pagamento da venda.
export function onlinePaymentState(
  payment: SalePayment,
  charge: OnlineCharge | undefined,
): { label: string; tone: 'warning' | 'positive' | 'neutral'; detail: string } {
  const due = charge ? formatCivilDate(charge.dueOn) : null;
  if (payment.status === 'CONFIRMED')
    return {
      label: 'Confirmado',
      tone: 'positive',
      detail: `Confirmado por ${payment.confirmedByName ?? 'provedor'}${
        charge?.paidOn ? ` em ${formatCivilDate(charge.paidOn)}` : ''
      } · cobrança gerada por ${charge?.createdByName ?? 'equipe'}`,
    };
  if (payment.status === 'REVERSED')
    return {
      label: 'Estornado',
      tone: 'neutral',
      detail: payment.reversalReason ?? 'Estornado no provedor.',
    };
  if (payment.status === 'CANCELED')
    return {
      label: charge?.status === 'EXPIRED' ? 'Vencida' : 'Cancelada',
      tone: 'neutral',
      detail: charge?.closedReason ?? payment.reversalReason ?? 'O valor voltou para em aberto.',
    };
  return {
    label: 'Aguardando pagamento',
    tone: 'warning',
    detail: `Gerada por ${charge?.createdByName ?? 'equipe'}${due ? ` · vence ${due}` : ''}`,
  };
}
